import { action, observable, isArrayLike } from 'mobx';
import { utils } from '@kurtosys/ksys-app-template';

import { Stores } from '../models/Stores';
import { DataStore } from './DataStore';

import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { ConditionalDisclaimer } from '../models/configuration/disclaimers/ConditionalDisclaimer';
import { ConditionalDisclaimers } from '../models/configuration/disclaimers/ConditionalDisclaimers';
import { ConditionalDisclaimersByKey } from '../models/configuration/disclaimers/ConditionalDisclaimersByKey';
import { DisclaimerDisplayType } from '../models/configuration/disclaimers/DisclaimerDisplayType';
import { DisclaimerElement } from '../models/configuration/DisclaimerElement';
import { DisclaimersByDocumentType } from '../models/configuration/disclaimers/DisclaimersByDocumentType';
import { RowConfig } from '../models/configuration/explorer/RowConfig';

export class ConditionalDisclaimerStore {
    @action
    public static initialize(sharedStores: Stores) {
        ConditionalDisclaimerStore.dataStore = sharedStores.data;
        ConditionalDisclaimerStore.initialized = true;
    }

    /**
     * Returns a list of conditional disclaimers related to the current module,
     * which consumes fund type tabs, module tabs, table columns (including documents).
     *
     * @static
     * @returns
     */
    public static getConfigForModule(): ConditionalDisclaimers {
        if (!ConditionalDisclaimerStore.initialized) {
            return [];
        }

        const response: ConditionalDisclaimers = [];
        if (ConditionalDisclaimerStore.dataStore) {
            const { config } = ConditionalDisclaimerStore.dataStore;
            if (config) {
                const { columnConfig } = config;
                response.push(
                    ...ConditionalDisclaimerStore.getFundTypeAndModuleTabsConditionals()
                );
                response.push(
                    ...ConditionalDisclaimerStore.getDocumentConditionals()
                );
                response.push(
                    ...ConditionalDisclaimerStore.getColumnConditionals(
                        columnConfig
                    )
                );
            }
        }
        return response;
    }

    /**
     * Returns a list of conditional disclaimers related to the current fund card,
     * which currently only refers to he fund facts.
     *
     * @static
     * @returns
     */
    public static getConfigForFundCard(): ConditionalDisclaimers {
        if (!ConditionalDisclaimerStore.initialized) {
            return [];
        }

        const response: ConditionalDisclaimers = [];
        if (ConditionalDisclaimerStore.dataStore) {
            const { config } = ConditionalDisclaimerStore.dataStore;
            if (config) {
                const rowConfig = config.rowConfig as RowConfig;
                if (rowConfig) {
                    response.push(
                        ...ConditionalDisclaimerStore.getFundFactConditionals(
                            rowConfig
                        )
                    );
                }
            }
        }
        return response;
    }

    /**
     * Returns a list of ConditionalDisclaimers based on the provided document type and optional culture
     *
     * @static
     * @param type
     * @param [culture]
     * @returns
     */
    public static getConditionalsForDocumentTypeAndCulture(
        type: string,
        culture?: string
    ): ConditionalDisclaimers {
        if (!ConditionalDisclaimerStore.initialized) {
            return [];
        }

        let response = [];

        if (type) {
            const disclaimersByDocumentType = ConditionalDisclaimerStore.documentTypeAndCultureConfig();
            if (disclaimersByDocumentType && disclaimersByDocumentType[type]) {
                const disclaimersByDocType = disclaimersByDocumentType[type];
                if (
                    culture &&
                    disclaimersByDocType.disclaimersByCulture &&
                    disclaimersByDocType.disclaimersByCulture[culture] &&
                    disclaimersByDocType.disclaimersByCulture[culture]
                        .conditionalDisclaimers
                ) {
                    response =
                        disclaimersByDocType.disclaimersByCulture[culture]
                            .conditionalDisclaimers;
                } else if (
                    disclaimersByDocumentType[type].conditionalDisclaimers &&
                    !culture
                ) {
                    response =
                        disclaimersByDocumentType[type].conditionalDisclaimers;
                }
            }
        }
        const isMobile = ConditionalDisclaimerStore.dataStore.isMobile;
        return !isMobile
            ? response
            : response.slice().map(disc => {
                  return {
                      ...disc,
                      display: DisclaimerDisplayType.superscript,
                  };
              });
    }

    /**
     * Convert old disclaimer config from `rowConfig` into the new ConditionalDisclaimer format,
     * This will omit the conditional & order field as they did not exist before.
     *
     * @static
     * @param rowConfigDisclaimer DisclaimerElement
     * @returns ConditionalDisclaimer
     */
    public static convertRowConfigDisclaimer(
        rowConfigDisclaimer: DisclaimerElement
    ): ConditionalDisclaimer {
        let displayType: any = DisclaimerDisplayType.superscript;
        if (!ConditionalDisclaimerStore.dataStore.isMobile) {
            if (!!rowConfigDisclaimer.display) {
                displayType = rowConfigDisclaimer.display;
            } else if (rowConfigDisclaimer.footnote === false) {
                displayType = DisclaimerDisplayType.tooltip;
            }
        }
        return {
            disclaimerType: rowConfigDisclaimer.disclaimerType,
            display: displayType,
            linked: rowConfigDisclaimer.linked,
            transforms: rowConfigDisclaimer.transforms,
        };
    }

    @observable.ref
    private static initialized: boolean = false;
    private static dataStore: DataStore;

    /**
     * Returns a list of ConditionalDisclaimers pulled from the rowConfig.fundFacts
     *
     * @private
     * @static
     * @param rowConfig
     * @returns
     */
    private static getFundFactConditionals(
        rowConfig: RowConfig
    ): ConditionalDisclaimers {
        const response = [];
        if (
            rowConfig &&
            rowConfig.fundFacts &&
            isArrayLike(rowConfig.fundFacts.cards) &&
            rowConfig.fundFacts.cards.length > 0
        ) {
            response.push(
                ...rowConfig.fundFacts.cards.reduce((disclaimers, card) => {
                    if (card.disclaimer) {
                        disclaimers.push(
                            ConditionalDisclaimerStore.convertRowConfigDisclaimer(
                                card.disclaimer
                            )
                        );
                    }
                    return disclaimers;
                }, [])
            );
        }
        return response;
    }

    /**
     * Returns a list of ConditionalDisclaimers pulled from the module table column configuration specified
     *
     * @private
     * @static
     * @param [columnConfig]
     * @returns ConditionalDisclaimers
     */
    private static getColumnConditionals(
        columnConfig?: Array<ColumnConfig>
    ): ConditionalDisclaimers {
        const response: ConditionalDisclaimers = [];
        if (isArrayLike(columnConfig) && columnConfig.length > 0) {
            columnConfig.forEach(column => {
                if (column) {
                    const { cellDisclaimers, type } = column;
                    if (isArrayLike(cellDisclaimers)) {
                        response.push(...cellDisclaimers);
                    }
                    if (['group', 'dropdown'].includes(type)) {
                        const { groups = [] } = column;
                        groups.forEach(group => {
                            response.push(
                                ...ConditionalDisclaimerStore.getColumnConditionals(
                                    group.columns
                                )
                            );
                        });
                    }
                }
            });
        }
        return response;
    }

    /**
     * Returns a list of ConditionalDisclaimers pulled from the FundType & Module Tabs configuration
     *
     * @private
     * @static
     * @returns ConditionalDisclaimers
     */
    private static getFundTypeAndModuleTabsConditionals(): ConditionalDisclaimers {
        const response: ConditionalDisclaimers = [];
        const { config, isMobile } = ConditionalDisclaimerStore.dataStore;
        const { fundTypes } = config;
        if (isArrayLike(fundTypes) && fundTypes.length > 0) {
            fundTypes.some(fundType => {
                if (fundType) {
                    // load disclaimers for all fund type tabs (as they will always will be visible)
                    const { disclaimers: fundTypeDisclaimers } = fundType;
                    if (isArrayLike(fundTypeDisclaimers)) {
                        // due to mobile showing dropdowns instead of tabs, we force tooltips to display as superscripts instead
                        if (isMobile) {
                            response.push(
                                ...ConditionalDisclaimerStore.setDisclaimersDisplay(
                                    fundTypeDisclaimers,
                                    DisclaimerDisplayType.superscript
                                )
                            );
                        } else {
                            response.push(...fundTypeDisclaimers);
                        }
                    }
                    // load all module disclaimers for the selected fund type
                    if (config.selectedFundType === fundType) {
                        const { modules } = fundType;
                        if (isArrayLike(modules) && modules.length > 0) {
                            modules.forEach(module => {
                                if (module) {
                                    const {
                                        disclaimers: moduleDisclaimers,
                                    } = module;
                                    if (isArrayLike(moduleDisclaimers)) {
                                        // due to mobile showing dropdowns instead of tabs, we force tooltips to display as superscripts instead
                                        if (isMobile) {
                                            response.push(
                                                ...ConditionalDisclaimerStore.setDisclaimersDisplay(
                                                    moduleDisclaimers,
                                                    DisclaimerDisplayType.superscript
                                                )
                                            );
                                        } else {
                                            response.push(...moduleDisclaimers);
                                        }
                                    }
                                }
                            });
                        }
                        // stop looping if you have found the active fund type
                        return true;
                    }
                }
            });
        }
        return response;
    }

    /**
     * Returns a list of ConditionalDisclaimers pulled from the document by type and culture configuration
     *
     * @private
     * @static
     * @returns ConditionalDisclaimers
     */
    private static getDocumentConditionals(): ConditionalDisclaimers {
        const conditionalsByDocType = ConditionalDisclaimerStore.documentTypeAndCultureConfig();
        const conditionals = ConditionalDisclaimerStore.flattenDocumentConditionals(
            conditionalsByDocType
        );
        return conditionals;
    }

    /**
     * Returns the merged configuration for module & global disclaimers by document type and culture
     *
     * @private
     * @static
     * @returns DisclaimersByDocumentType
     */
    private static documentTypeAndCultureConfig(): DisclaimersByDocumentType {
        if (ConditionalDisclaimerStore.dataStore) {
            const { config } = ConditionalDisclaimerStore.dataStore;
            if (config) {
                const globalDisclaimers =
                    config.globalDisclaimersByDocumentType;
                const moduleDisclaimers = config.disclaimersByDocumentType;
                return utils.object.deepMergeObjectsWithOptions(
                    {
                        arrayMergeStrategy: 'DeepMergeByKeyOrAppend',
                        getKeyFn: e => `${e.disclaimerType}-${e.display}`,
                    },
                    globalDisclaimers,
                    moduleDisclaimers
                );
            }
        }
    }

    /**
     * Used to return a list of ConditionalDisclaimers from the nested document by type and culture configuration
     *
     * @private
     * @static
     * @param conditionalsByKey
     * @returns ConditionalDisclaimers
     */
    private static flattenDocumentConditionals(
        conditionalsByKey: ConditionalDisclaimersByKey<{
            disclaimersByCulture?: ConditionalDisclaimersByKey;
        }>
    ): ConditionalDisclaimers {
        const isMobile = ConditionalDisclaimerStore.dataStore.isMobile;
        const conditionalDisclaimers = [];
        if (conditionalsByKey) {
            const keys = Object.keys(conditionalsByKey);
            keys.forEach(key => {
                const keyObj = conditionalsByKey[key];
                if (keyObj) {
                    if (keyObj.conditionalDisclaimers) {
                        const partialConditionalDisclaimers = isMobile
                            ? keyObj.conditionalDisclaimers.map(
                                  conditionalDisclaimer => {
                                      return {
                                          ...conditionalDisclaimer,
                                          display:
                                              DisclaimerDisplayType.superscript,
                                      };
                                  }
                              )
                            : keyObj.conditionalDisclaimers;
                        conditionalDisclaimers.push(
                            ...partialConditionalDisclaimers
                        );
                    }
                    if (keyObj.disclaimersByCulture) {
                        conditionalDisclaimers.push(
                            ...ConditionalDisclaimerStore.flattenDocumentConditionals(
                                keyObj.disclaimersByCulture
                            )
                        );
                    }
                }
            });
        }
        return conditionalDisclaimers;
    }

    /**
     * Iterates over a list of ConditionalDisclaimers updating them to the specified display type
     *
     * @private
     * @static
     * @param disclaimers ConditionalDisclaimers
     * @param type DisclaimerDisplayType
     * @returns ConditionalDisclaimers
     */
    private static setDisclaimersDisplay(
        disclaimers: ConditionalDisclaimers,
        type: ConditionalDisclaimer['display']
    ): ConditionalDisclaimers {
        const conditionalDisclaimers = disclaimers.map(disclaimer => {
            return { ...disclaimer, display: type };
        });
        return observable(conditionalDisclaimers);
    }
}



// WEBPACK FOOTER //
// ./src/stores/ConditionalDisclaimerStore.tsx